import { defineStore } from "pinia";
import { computed } from "vue";
import { LoyaltyStickerSubtype } from "./loyalty-sticker-models";
import { useNgSession } from "@sazka/ng/ng-session";
import { usePopupsSequence } from "../../../shared/components/master-page/use-popups-sequence";
import { useLoyaltyPlayerStatus } from "../../composables/use-loyalty-player-status";
import { LoyaltyStickerType } from "@/loyalty/api/models/loyalty-api-models";

const anonymousExactPages = ["/loterie", "/losy"];
const anonymousSubtreePages = ["/loterie/", "/losy/"];
const anonymousEnabledVertical = ["elosy"];
const anonymousExcludedPages = ["/losy/stiraci"];
const anonymousExcludedSubtreePages = [
    "/loterie/klasicke-loterie/power-spin/",
    "/loterie/klasicke-loterie/sportka/losovani",
    "/loterie/klasicke-loterie/extra-renta/losovani",
    "/loterie/klasicke-loterie/euromiliony/losovani",
    "/loterie/klasicke-loterie/stastnych-10/losovani",
];

const config = loyaltyStickerConfig?.settings;

export const useLoyaltySticker = defineStore("loyalty-sticker", () => {
    const { isLoggedIn } = useNgSession();
    const { isLoyaltyStickerVisible } = usePopupsSequence();

    const isAnonymousSticker = computed(
        () =>
            !isLoggedIn.value &&
            !environment.isRegistered &&
            isLoyaltyStickerVisible.value &&
            (anonymousExactPages.some((page) => page == environment.nodeAliasPath) ||
                anonymousSubtreePages.some((page) => environment.nodeAliasPath?.startsWith(page)) ||
                anonymousEnabledVertical.some((vertical) => vertical == window.landingPageVertical)) &&
            anonymousExcludedPages.every((page) => page !== environment.nodeAliasPath) &&
            anonymousExcludedSubtreePages.every((page) => !environment.nodeAliasPath?.startsWith(page))
    );

    const stickerSubtype = computed(() => {
        if (isAnonymousSticker.value) {
            if (environment.nodeAliasPath == "/loterie" || environment.nodeAliasPath?.startsWith("/loterie/")) {
                return LoyaltyStickerSubtype.lotteryBounus;
            } else if (
                (environment.nodeAliasPath == "/losy" ||
                    environment.nodeAliasPath?.startsWith("/losy/") ||
                    window.landingPageVertical === "elosy") &&
                !environment.nodeAliasPath?.startsWith("/losy/stiraci")
            ) {
                return LoyaltyStickerSubtype.elosBonus;
            }
        }

        return null;
    });

    const stickerConfigType = computed<LoyaltyStickerType>(() => {
        if (isAnonymousSticker.value) return "Anonymous";

        if (!config || !isLoggedIn.value) return "None";

        const nodeAliasPath = environment.nodeAliasPath.toLocaleLowerCase();

        const exactPageStickerType = config.pages[nodeAliasPath];
        if (exactPageStickerType) {
            return exactPageStickerType;
        }

        const subPathKey = Object.keys(config.paths)
            .sort((a, b) => b.split("/").length - a.split("/").length)
            .find((x) => nodeAliasPath.startsWith(x.toLowerCase()));

        if (subPathKey) {
            return config.paths[subPathKey];
        }

        return "None";
    });

    const { loyaltyPlayerStatus, bonusomatPlayerStatus, fsPlayerStatus, gwPlayerStatus } =
        useLoyaltyPlayerStatus("ALL");

    const stickerType = computed<Omit<LoyaltyStickerType, "LoyaltyProgress">>(() => {
        if (stickerConfigType.value != "LoyaltyProgress") return stickerConfigType.value;

        if (!loyaltyPlayerStatus.value) return "None";

        if (loyaltyPlayerStatus.value.vertical == "Casino") {
            if (
                fsPlayerStatus.fsPlayerData.value.access &&
                fsPlayerStatus.hasMaxPoints.value &&
                !bonusomatPlayerStatus.hasMaxPoints.value
            ) {
                return "FurrySpin";
            } else if (
                gwPlayerStatus.gwPlayerData.value.access &&
                gwPlayerStatus.hasMaxPoints.value &&
                !bonusomatPlayerStatus.hasMaxPoints.value
            ) {
                return "GoldenWheel";
            } else if (bonusomatPlayerStatus.bonusomatPlayerData.value.access) {
                return "Bonusomat";
            }
        } else if (
            // else FOR losy && loterie && null
            loyaltyPlayerStatus.value.vertical == "Draw-Based" ||
            loyaltyPlayerStatus.value.vertical == "Scratchcards"
        ) {
            if (
                bonusomatPlayerStatus.bonusomatPlayerData.value.access &&
                bonusomatPlayerStatus.hasMaxPoints.value &&
                !gwPlayerStatus.hasMaxPoints.value
            ) {
                return "Bonusomat";
            } else if (
                fsPlayerStatus.fsPlayerData.value.access &&
                fsPlayerStatus.hasMaxPoints.value &&
                !gwPlayerStatus.hasMaxPoints.value
            ) {
                return "FurrySpin";
            } else if (gwPlayerStatus.gwPlayerData.value.access) {
                return "GoldenWheel";
            }
        }

        return "None";
    });

    return { stickerType, stickerSubtype };
});
